import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getActiveBenefits } from '@epi-actions/api';
import { IBenefits, IBenefitGroup } from '@epi-models/pages/BenefitGroups';
import { getOnboardingLoadBenefitPageType } from '@epi-store/onboarding/onboarding.selectors';
import { useOnboardingWizard } from '@epi-shared/hooks';
import { LoadBenefitPageTypes } from '@epi-constants/onboardingTypes';
import { BenefitType } from '@epi-constants/benefitTypes';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';

export const useTopup = () => {
  const dispatch = useDispatch();
  const { isOnBoardingActive } = useOnboardingWizard();
  const benefitGroups = useGetBenefitGroupsQuery();
  const onboardingPageType = useSelector(getOnboardingLoadBenefitPageType);
  const [isLoading, setIsLoading] = useState(true);

  const sortBenefitsInOrder = (benefits: IBenefits[]): IBenefits[] => {
    const orderMap = {
      lunch: 1,
      recreational: 2,
      transport: 3,
      massage: 4,
      wellbeing: 5
    };

    return [...benefits]
      .map(elem => elem.toLowerCase())
      .sort((a, b) => {
        const orderA = orderMap[a] || 999;
        const orderB = orderMap[b] || 999;
        return orderA - orderB;
      }) as IBenefits[];
  };

  useEffect(() => {
    dispatch(getActiveBenefits());
  }, []);

  useEffect(() => {
    if (benefitGroups?.data) {
      setIsLoading(false);
    }
  }, [benefitGroups]);

  const filterBenefits = (benefits: IBenefits[]) => {
    if (!isOnBoardingActive) {
      return benefits;
    }
    if (onboardingPageType === LoadBenefitPageTypes.Lounari) {
      return benefits.filter(benefit => benefit === BenefitType.Lunch);
    }
    if (onboardingPageType === LoadBenefitPageTypes.Others) {
      return benefits.filter(benefit => benefit !== BenefitType.Lunch);
    }

    return benefits;
  };

  const getBenefitsFromBenefitGroups = (
    benefitGroups?: IBenefitGroup[]
  ): IBenefits[] => {
    if (!benefitGroups) return [];

    const benefits = Array.from(
      new Set(
        benefitGroups
          .filter(group => group.beneficiaryCount > 0)
          .flatMap(group =>
            group.benefitSettings.map(setting => setting.benefitType)
          )
      )
    ) as IBenefits[];
    return sortBenefitsInOrder(benefits);
  };

  const returnedBenefits = filterBenefits(
    getBenefitsFromBenefitGroups(benefitGroups.data)
  );

  return {
    hasLoadableBenefit: !!returnedBenefits.length,
    isLoading,
    activeBenefits: returnedBenefits
  };
};
